<template>
  <transition name="fade-lift" appear>
    <div
      v-if="visible"
      class="chat-bubble"
      aria-live="polite"
      ref="bubble"
    >
      <span v-if="!shownText" class="typing" aria-label="Assistant is typing">
        <span>.</span><span>.</span><span>.</span>
      </span>
      <span v-else v-html="shownText"></span>
    </div>
  </transition>
</template>

<script>
export default {
  name: "StreamingMessage",
  props: {
    fullText: { type: String, required: true },
    chunkMs: { type: Number, default: 20 },
    mode: { type: String, default: "word" },
    startDelayMs: { type: Number, default: 0 }
  },
  data() {
    return {
      shownText: "",
      chunks: [],
      i: 0,
      raf: null,
      reduced: false,
      visible: true,
      lastTs: 0,
      carry: 0,
      started: false
    };
  },
  created() {
    if (typeof window !== "undefined" && window.matchMedia) {
      try {
        this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      } catch (e) {}
    }
  },
  mounted() {
    this.resetAndStart();
  },
  beforeDestroy() {
    this.cancel();
  },
  watch: {
    fullText() {
      this.resetAndStart();
    }
  },
  methods: {
    splitText(text) {
      if (this.mode === "sentence") {
        const m = text.match(/[^.?!\n]+[.?!]|\S+$/g);
        return m ? m : [text];
      }
      if (this.mode === "paragraph") {
        return text.split(/(\n\n+)/); // keep the separators so spacing stays natural
      }
      if (this.mode === "char") {
        return text.split("");
      }
      // word mode: include spaces as tokens so spacing is preserved
      return text.split(/(\s+)/);
    },
    resetAndStart() {
      this.cancel();
      this.shownText = "";
      this.i = 0;
      this.started = false;
      this.chunks = this.splitText(this.fullText);

      if (this.reduced) {
        // accessibility: no streaming
        this.shownText = this.fullText;
        this.$emit("done");
        return;
      }

      const kick = () => {
        this.lastTs = performance.now();
        this.carry = 0;
        this.started = true;
        this.raf = requestAnimationFrame((t) => this.tick(t));
      };

      if (this.startDelayMs > 0) {
        setTimeout(kick, this.startDelayMs);
      } else {
        kick();
      }
    },
    tick(now) {
      const dt = now - this.lastTs;
      this.lastTs = now;
      this.carry += dt;

      const perChunk = this.chunkMs;
      let advanced = 0;

      while (this.carry >= perChunk && this.i < this.chunks.length) {
        this.carry -= perChunk;
        this.i += 1;
        advanced++;
      }

      if (advanced > 0) {
        this.shownText = this.chunks.slice(0, this.i).join("");
        this.$emit("update");
      }

      if (this.i < this.chunks.length) {
        this.raf = requestAnimationFrame((t) => this.tick(t));
      } else {
        this.cancel();
        this.$emit("done");
      }
    },
    cancel() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }
  }
};
</script>

<style scoped>
.fade-lift-enter-active,
.fade-lift-leave-active {
  transition:
    opacity .28s cubic-bezier(.2,.8,.2,1),
    transform .28s cubic-bezier(.2,.8,.2,1),
    filter .28s cubic-bezier(.2,.8,.2,1);
}
.fade-lift-enter { opacity: 0; transform: translateY(12px); filter: blur(4px); }
.fade-lift-leave-to { opacity: 0; transform: translateY(6px); filter: blur(3px); }

.chat-bubble {
  word-wrap: break-word;
  will-change: transform, opacity, filter;
}

/* Typing dots */
.typing span {
  display: inline-block;
  animation: typing-bounce 1s infinite;
}
.typing span:nth-child(2) { animation-delay: .15s; }
.typing span:nth-child(3) { animation-delay: .30s; }
@keyframes typing-bounce {
  0%,80%,100% { transform: translateY(0); opacity: .35; }
  40% { transform: translateY(-3px); opacity: 1; }
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  .fade-lift-enter-active,
  .fade-lift-leave-active { transition: none !important; }
}
</style>